import React, { FC } from 'react';

import { useSelector } from 'react-redux';

import { BaseStyleEnums, IdEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import { PlayerPosition } from '~/core/enums';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { StateType } from '~/redux/types';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';

import { useGamePreviewQuery } from '../../../../api';
import GameInfoReports from '../../../../components/gameInfoReports/GameInfoReports';
import GamePreview from '../../../../components/gamePreview/GamePreview';
import GoalieComparison from '../../../../components/goalieComparison/GoalieComparison';
import LastTenGames from '../../../../components/lastTenGames/LastTenGames';
import PlayersToWatch from '../../../../components/playersToWatch/PlayersToWatch';
import PlayoffsRecord from '../../../../components/playoffsRecord/PlayoffsRecord';
import SeasonSeries from '../../../../components/seasonSeries/SeasonSeries';
import TeamRoster, { PlayersByPosition } from '../../../../components/teamRoster/TeamRoster';
import TeamStats from '../../../../components/teamStats/TeamStats';
import { LOADING_DISPLAY_SIZE, LoadingContainer } from '../../../../styles';
import { LandingGame, SkaterSeasonStat } from '../../../../types';
import GameCenterLoader from '../../../gameCenterLoader/GameCenterLoader';
import { AdContainer } from '../../../rightRail/styles';

import { getPregameComponentFlags } from './utils';

import { MainContent, PregameContainer, RightRail } from './styles';

type Props = {
    game: LandingGame;
    gameId?: number;
    isInitialLoad: boolean;
};

const PreGame: FC<Props> = ({ game, gameId, isInitialLoad }) => {
    const rightRail = useSelector((state: StateType) => state.rightRail);
    const screenSize = useSizeWithThrottle();
    const isLarge = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;

    const gamePreviewAPIResponse = useGamePreviewQuery(gameId);

    const seasonSeriesFullCoverageUrl = useOptionForLocale(rightRail.seasonSeriesFullCoverageUrl, true);

    const getPlayers = (teamId: number): PlayersByPosition => {
        const getSkaters = (isDefense: boolean): Array<SkaterSeasonStat> =>
            game?.matchup?.skaterSeasonStats?.skaters?.filter(
                (skater) =>
                    skater.teamId === teamId &&
                    (isDefense
                        ? skater.position === PlayerPosition.defensemen
                        : skater.position !== PlayerPosition.defensemen)
            ) || [];

        return {
            forwards: getSkaters(false),
            defense: getSkaters(true),
            goalies: game?.matchup?.goalieSeasonStats?.goalies?.filter((goalie) => goalie.teamId === teamId) || [],
        };
    };

    const pregameComponentFlags = getPregameComponentFlags(game, gamePreviewAPIResponse);
    const mainContent = (
        <GameCenterLoader isInitialLoad={isInitialLoad}>
            {pregameComponentFlags.isPlayersToWatchVisible && <PlayersToWatch game={game} />}
            {pregameComponentFlags.isGoalieComparisonVisible && <GoalieComparison game={game} />}
            <TeamRoster
                contextDescriptor={game.matchup?.skaterSeasonStats}
                game={game}
                getPlayers={getPlayers}
                pregameComponentFlags={pregameComponentFlags}
            />
        </GameCenterLoader>
    );

    const rightRailContent =
        game.id && rightRail?.gameInfo ? (
            <>
                {rightRail?.seasonSeries && rightRail.seasonSeries.length > 0 && (
                    <SeasonSeries
                        game={game}
                        seasonSeries={rightRail.seasonSeries}
                        seasonSeriesWins={rightRail.seasonSeriesWins}
                        seasonSeriesFullCoverageUrl={seasonSeriesFullCoverageUrl}
                    />
                )}
                <GamePreview gamePreviewAPIResponse={gamePreviewAPIResponse} />
                {window.__AD_SETTINGS__.mrecDivId && (
                    <AdContainer>
                        <AdTag divId={window.__AD_SETTINGS__.mrecDivId} adType={AdType.MEDIUM_RECTANGLE} forceReload />
                    </AdContainer>
                )}
                <TeamStats game={game} teamSeasonStats={rightRail?.teamSeasonStats} />
                {game.gameType === IdEnums.GameType.playoffs && rightRail?.playoffsRecord ? (
                    <PlayoffsRecord game={game} playoffsRecord={rightRail.playoffsRecord} />
                ) : (
                    <LastTenGames lastTen={rightRail?.last10Record} game={game} />
                )}
                <GameInfoReports game={game} gameInfo={rightRail?.gameInfo} />
            </>
        ) : (
            <LoadingContainer>
                <LoadingDisplay isInline isLoading size={LOADING_DISPLAY_SIZE} />
            </LoadingContainer>
        );

    return isLarge ? (
        <PregameContainer $pregameComponentFlags={pregameComponentFlags}>
            <MainContent>{mainContent}</MainContent>
            <RightRail>{rightRailContent}</RightRail>
        </PregameContainer>
    ) : (
        <PregameContainer $pregameComponentFlags={pregameComponentFlags}>
            {mainContent}
            {rightRailContent}
        </PregameContainer>
    );
};

export default PreGame;
